import { lazyWithRetries } from '@globals/lazyWithRetries'
import { colors } from '@globals/themes/defaultTheme'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import HiddenFileInput from '@shared/components/mediaUploads/HiddenFileInput'
import { MAX_FILE_SIZE_IN_MB, getZeroSizeValidationMsg } from '@shared/uploads/ImageUploads.util'
import { T, useT } from '@transifex/react'
import { ReactNode, Suspense } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import ErrorText from '../ErrorText'
import FieldLabel from '../FieldLabel'
import HelpText from '../HelpText'
import { getRichTextLength } from './getRichTextLength'
import { getRichTextEditorFieldStyles } from './richTextEditorFieldStyles'
import { AttachmentUploadFn } from './useFroalaAttachments'
import { useRichTextEditorField } from './useRichTextEditorField'
import { ImageUploadFn } from './useRichTextImageUpload'

// The Froala component, its JS/CSS plugin assets, and the license key setup all
// live in LazyFroalaEditor so they load only when an editor first mounts,
// keeping Froala out of bundles (e.g. the home feed) that merely reference this
// field without ever rendering the editor. Use lazyWithRetries (rather than
// plain react.lazy()) so a stale/failed vendor-froala chunk after e.g. a new deploy
// retries and ultimately reloads the page instead of throwing an uncaught error in rollbar.
const FroalaEditor = lazyWithRetries(() => import('./LazyFroalaEditor'))

// Lazy so the Giphy SDK and search UI stay out of the bundle for tenants that
// have GIFs turned off, where this never renders.
const GiphySearchDialog = lazyWithRetries(
  () =>
    import(
      '@tenant/recognition/RecognitionModal/sendRecognitionView/giphySearch/GiphySearchDialog'
    ),
)

const editorFooterSx: SxProps = {
  position: 'absolute',
  bottom: 8,
  left: 16,
  right: 16,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  pointerEvents: 'none',
}

export type RichTextEditor = {
  image: {
    insert: (url: string, text: string | null, convert: boolean | null, figure: null) => void
    get: () => null
  }
  html?: {
    get: () => string
    set: (html: string) => void
  }
  edit: {
    off: () => void
    on: () => void
  }
  popups: {
    hideAll: () => void
  }
  $el: HTMLElement & { [key: string]: HTMLElement }
}

export type RichTextEditorFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label?: React.ReactNode
  minHeight?: number
  help?: React.ReactNode
  placeholder?: string
  required?: boolean
  disabled?: boolean
  labelStyle?: SxProps
  imageUploadFn?: ImageUploadFn
  areGifsSupported?: boolean
  areMentionsSupported?: boolean
  attachmentUploadFn?: AttachmentUploadFn
  attachmentFieldName?: string
  gifFieldName?: string
  gifModalId?: string
  isFullVersion?: boolean
  charCounterMax: number
  autoFocus?: boolean
  /**
   * When provided, this node is rendered as the "refine with AI" affordance
   * inside the editor footer. Each entity (recognition, award, etc.) supplies
   * its own button so the editor stays decoupled from entity-specific logic.
   */
  refineButton?: ReactNode
  /**
   * When true, the editor starts compact (small height, no toolbar).
   * On first focus/click it expands to full height and shows the toolbar.
   */
  compactUntilFocus?: boolean
  /**
   * Called when the editor expands or collapses (only relevant when compactUntilFocus is true).
   */
  onExpandedChange?: (expanded: boolean) => void
  /**
   * Called when the user presses Enter with the editor's current HTML (so the form can use it
   * without relying on sync with form state). When compactUntilFocus is true, this is still
   * called and the editor is also collapsed.
   */
  onEnterSubmit?: (currentValue: string) => void
  /**
   * Used for conditional styling in compact mode
   */
  hasTextContent?: boolean
  /**
   * The expanded state of editor in compact mode
   */
  isCompactEditorExpanded?: boolean
}

const RichTextEditorField = <T extends FieldValues>({
  autoFocus = false,
  control,
  label = null,
  help = null,
  name,
  minHeight = 150,
  placeholder,
  required = true,
  disabled,
  labelStyle,
  imageUploadFn,
  areGifsSupported = false,
  areMentionsSupported = false,
  attachmentUploadFn,
  attachmentFieldName = 'attachments',
  gifFieldName = 'gif',
  gifModalId = 'gifModal',
  isFullVersion = true,
  charCounterMax,
  refineButton,
  compactUntilFocus = false,
  onExpandedChange,
  onEnterSubmit,
  hasTextContent,
  isCompactEditorExpanded,
}: RichTextEditorFieldProps<T>) => {
  const t = useT()
  const {
    editorConfig,
    isExpanded,
    isCompact,
    editorContainerRef,
    onChangeRef,
    areGifsAvailable,
    isGifPreferenceLoading,
    giphyDialogId,
    handleGifSelected,
    attachmentHook,
  } = useRichTextEditorField({
    name,
    control,
    autoFocus,
    placeholder,
    disabled,
    minHeight,
    imageUploadFn,
    areGifsSupported,
    areMentionsSupported,
    attachmentUploadFn,
    attachmentFieldName,
    gifFieldName,
    gifModalId,
    isFullVersion,
    charCounterMax,
    compactUntilFocus,
    onExpandedChange,
    onEnterSubmit,
    isCompactEditorExpanded,
  })

  const editorSkeleton = <Skeleton variant="rectangular" height={150} width="100%" />

  return (
    <Box>
      <style>{getRichTextEditorFieldStyles(colors)}</style>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error, isDirty } }) => {
          // Store onChange in ref so useFroalaGifs can access it
          onChangeRef.current = onChange
          const showError = Boolean(error) && (!isCompact || isDirty)
          const currentLength = getRichTextLength(value || '')
          const isOverLimit = currentLength > charCounterMax
          return (
            <>
              {label && (
                <FieldLabel
                  htmlFor={name}
                  required={required}
                  disabled={disabled}
                  labelStyle={labelStyle}>
                  {label}
                </FieldLabel>
              )}
              {help && <HelpText>{help}</HelpText>}
              <Box
                ref={editorContainerRef}
                sx={{ position: 'relative' }}
                {...(isCompact && { 'data-rich-text-compact': 'true' })}
                {...(isCompact &&
                  hasTextContent && { 'data-rich-text-compact-with-content': 'true' })}>
                <Suspense fallback={editorSkeleton}>
                  {/* Froala clones its config when it initializes and ignores later
                      changes, so a GIF editor's toolbar has to be built with the
                      tenant setting already known. Only those editors wait: a
                      sibling fetch of the shared preference must not delay
                      award/announcement fields that never offer GIFs. */}
                  {areGifsSupported && isGifPreferenceLoading ? (
                    editorSkeleton
                  ) : (
                    <FroalaEditor
                      key={compactUntilFocus && !isExpanded ? 'compact' : 'expanded'}
                      tag="textarea"
                      config={editorConfig}
                      model={value || ''}
                      onModelChange={onChange}
                    />
                  )}
                </Suspense>
                {(refineButton || !isCompact) && (
                  <Box sx={editorFooterSx}>
                    {refineButton && (
                      <Box sx={{ minWidth: 0, pointerEvents: 'auto' }}>{refineButton}</Box>
                    )}
                    {!isCompact && (
                      <Typography
                        component="div"
                        variant="caption"
                        sx={{
                          flexShrink: 0,
                          ml: 'auto',
                          color: isOverLimit ? 'error.main' : 'text.secondary',
                        }}>
                        {currentLength.toLocaleString()} / {charCounterMax.toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              {areGifsAvailable && (
                <Suspense fallback={null}>
                  <GiphySearchDialog onGifSelected={handleGifSelected} id={giphyDialogId} />
                </Suspense>
              )}
              {attachmentHook && (
                <HiddenFileInput
                  ref={attachmentHook.inputFileRef}
                  type="file"
                  accept="image/jpg, .png, .webp, .pdf, .docx, .pptx, .gif"
                  id="fileUploadAttachment"
                  onChange={attachmentHook.handleFileInputChange}
                />
              )}
              {attachmentHook?.maxSizeError && (
                <ErrorText>
                  <T
                    _str="The file exceeds the {maxSize}MB size limit, upload a smaller file"
                    maxSize={MAX_FILE_SIZE_IN_MB.toLocaleString()}
                  />
                </ErrorText>
              )}
              {attachmentHook?.invalidTypeError && (
                <ErrorText>
                  <T _str="Attachment must be JPG, PNG, GIF, WEBP, PDF, DOCX or PPTX" />
                </ErrorText>
              )}
              {attachmentHook?.emptyFileError && (
                <ErrorText>{getZeroSizeValidationMsg(t)}</ErrorText>
              )}
              {error && (
                <Box
                  sx={{
                    mt: 1.5,
                    minHeight: 22,
                    display: showError ? 'block' : 'none',
                  }}>
                  <ErrorText>{error.message}</ErrorText>
                </Box>
              )}
            </>
          )
        }}
      />
    </Box>
  )
}

export default RichTextEditorField
